import type { ReactNode } from "react";
import { Box } from "@mui/material";
import type { Promisable } from "type-fest";

import { PopupDialog } from "../PopupDialog/PopupDialog.js";
import {
  TortusEmbedSurface,
  useTortusEmbed,
  type TortusAvtLaunchToken,
  type TortusEmbedCallbacks,
} from "./TortusEmbedSurface.js";

export type {
  TortusAvtConsultationCompletedData,
  TortusAvtConsultationErrorData,
  TortusAvtConsultationLifecycleData,
  TortusAvtConsultationStartedData,
  TortusAvtLaunchToken,
} from "./TortusEmbedSurface.js";

export type TortusAvtModalProps = TortusEmbedCallbacks & {
  open: boolean;
  onClose: () => void;
  /**
   * Async function that returns a fresh launch token. Called by the Tortus SDK
   * on init and on any subsequent token refresh. In production this hits
   * aspire-server; in Storybook it's a fake.
   */
  tokenProvider: () => Promisable<TortusAvtLaunchToken>;
  /**
   * Slot rendered in the modal's header toolbar, to the left of the Close
   * button. Use this to surface controls that need to be clickable while the
   * modal is open (e.g. "Start consultation", "Go home").
   */
  headerActions?: ReactNode;
};

/**
 * Dialog shell around the Tortus AVT embed: the narrow-screen presentation, where
 * there is no room to sit beside a form. PopupDialog renders it full-screen on
 * phones. Where there is room, prefer {@link TortusAvtDrawer}, which leaves the
 * form readable and editable alongside.
 *
 * Presentational: it does not call the backend itself. Callers pass a
 * `tokenProvider` that returns the launch token — use
 * {@link useTortusLaunchToken} in real app code.
 */
export const TortusAvtModal = ({
  open,
  onClose,
  tokenProvider,
  headerActions,
  ...callbacks
}: TortusAvtModalProps) => {
  const { containerId, status, errorMessage } = useTortusEmbed({
    open,
    tokenProvider,
    callbacks,
  });

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      hasPadding={false}
      testId="tortusAvtModal"
    >
      <Box
        sx={{
          width: "100%",
          height: "90vh",
          minHeight: 600,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TortusEmbedSurface
          containerId={containerId}
          status={status}
          errorMessage={errorMessage}
          onClose={onClose}
          headerActions={headerActions}
        />
      </Box>
    </PopupDialog>
  );
};
